// Series Service - Firestore CRUD operations for investment series
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Series } from '../types';

const SERIES_COLLECTION = 'series';

/**
 * Create a new series in Firestore
 */
export const createSeries = async (seriesData: Omit<Series, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, SERIES_COLLECTION), {
      ...seriesData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating series:', error);
    throw new Error('Failed to create series');
  }
};

/**
 * Get all series from Firestore
 */
export const getAllSeries = async (): Promise<Series[]> => {
  try {
    const querySnapshot = await getDocs(
      query(collection(db, SERIES_COLLECTION), orderBy('issueDate', 'desc'))
    );
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        closingDate: data.closingDate?.toDate?.() || data.closingDate,
        issueDate: data.issueDate?.toDate?.() || data.issueDate,
        lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      } as Series;
    });
  } catch (error) {
    console.error('Error getting series:', error);
    throw new Error('Failed to fetch series');
  }
};

/**
 * Get a single series by ID
 */
export const getSeriesById = async (seriesId: string): Promise<Series | null> => {
  try {
    const docRef = doc(db, SERIES_COLLECTION, seriesId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        closingDate: data.closingDate?.toDate?.() || data.closingDate,
        issueDate: data.issueDate?.toDate?.() || data.issueDate,
        lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      } as Series;
    }
    return null;
  } catch (error) {
    console.error('Error getting series:', error);
    throw new Error('Failed to fetch series');
  }
};

/**
 * Get all series for a specific lender
 */
export const getSeriesByLender = async (lenderId: string): Promise<Series[]> => {
  try {
    const q = query(
      collection(db, SERIES_COLLECTION),
      where('lenderId', '==', lenderId),
      orderBy('issueDate', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        closingDate: data.closingDate?.toDate?.() || data.closingDate,
        issueDate: data.issueDate?.toDate?.() || data.issueDate,
        lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      } as Series;
    });
  } catch (error) {
    console.error('Error getting series by lender:', error);
    throw new Error('Failed to fetch lender series');
  }
};

/**
 * Get series by closing date
 */
export const getSeriesByClosingDate = async (closingDate: Date): Promise<Series[]> => {
  try {
    const q = query(
      collection(db, SERIES_COLLECTION),
      where('closingDate', '==', Timestamp.fromDate(closingDate))
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        closingDate: data.closingDate?.toDate?.() || data.closingDate,
        issueDate: data.issueDate?.toDate?.() || data.issueDate,
        lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      } as Series;
    });
  } catch (error) {
    console.error('Error getting series by closing date:', error);
    throw new Error('Failed to fetch series by closing date');
  }
};

/**
 * Update a series in Firestore
 */
export const updateSeries = async (
  seriesId: string,
  updates: Partial<Omit<Series, 'id'>>
): Promise<void> => {
  try {
    const docRef = doc(db, SERIES_COLLECTION, seriesId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating series:', error);
    throw new Error('Failed to update series');
  }
};

/**
 * Delete a series from Firestore
 */
export const deleteSeries = async (seriesId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, SERIES_COLLECTION, seriesId));
  } catch (error) {
    console.error('Error deleting series:', error);
    throw new Error('Failed to delete series');
  }
};

/**
 * Get active series (within lock-up period)
 */
export const getActiveSeries = async (): Promise<Series[]> => {
  try {
    const allSeries = await getAllSeries();
    const now = new Date();
    
    return allSeries.filter(series => {
      const lockUpEnd = new Date(series.lockupEnd);
      return now < lockUpEnd;
    });
  } catch (error) {
    console.error('Error getting active series:', error);
    throw new Error('Failed to fetch active series');
  }
};

/**
 * Get series past lock-up period
 */
export const getRedeemableSeries = async (): Promise<Series[]> => {
  try {
    const allSeries = await getAllSeries();
    const now = new Date();
    
    return allSeries.filter(series => {
      const lockUpEnd = new Date(series.lockupEnd);
      return now >= lockUpEnd;
    });
  } catch (error) {
    console.error('Error getting redeemable series:', error);
    throw new Error('Failed to fetch redeemable series');
  }
};

/**
 * Subscribe to real-time series updates
 */
export const subscribeSeries = (
  callback: (series: Series[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(collection(db, SERIES_COLLECTION), orderBy('issueDate', 'desc'));
  
  return onSnapshot(
    q,
    (snapshot) => {
      const series = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          closingDate: data.closingDate?.toDate?.() || data.closingDate,
          issueDate: data.issueDate?.toDate?.() || data.issueDate,
          lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        } as Series;
      });
      callback(series);
    },
    (error) => {
      console.error('Error in series subscription:', error);
      if (onError) {
        onError(new Error('Failed to subscribe to series'));
      }
    }
  );
};

/**
 * Subscribe to series for a specific lender
 */
export const subscribeLenderSeries = (
  lenderId: string,
  callback: (series: Series[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(
    collection(db, SERIES_COLLECTION),
    where('lenderId', '==', lenderId),
    orderBy('issueDate', 'desc')
  );
  
  return onSnapshot(
    q,
    (snapshot) => {
      const series = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          closingDate: data.closingDate?.toDate?.() || data.closingDate,
          issueDate: data.issueDate?.toDate?.() || data.issueDate,
          lockupEnd: data.lockupEnd?.toDate?.() || data.lockupEnd,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        } as Series;
      });
      callback(series);
    },
    (error) => {
      console.error('Error in lender series subscription:', error);
      if (onError) {
        onError(new Error('Failed to subscribe to lender series'));
      }
    }
  );
};

/**
 * Update series NAV and High Water Mark
 */
export const updateSeriesNAV = async (
  seriesId: string,
  newNAV: number,
  newHighWaterMark?: number
): Promise<void> => {
  try {
    const updates: any = {
      currentNAV: newNAV,
      updatedAt: Timestamp.now(),
    };
    
    if (newHighWaterMark !== undefined) {
      updates.highWaterMark = newHighWaterMark;
    }
    
    await updateSeries(seriesId, updates);
  } catch (error) {
    console.error('Error updating series NAV:', error);
    throw new Error('Failed to update series NAV');
  }
};

/**
 * Calculate total AUM across all series
 */
export const calculateTotalAUM = async (): Promise<number> => {
  try {
    const allSeries = await getAllSeries();
    return allSeries.reduce((total, series) => total + series.currentNAV, 0);
  } catch (error) {
    console.error('Error calculating total AUM:', error);
    throw new Error('Failed to calculate total AUM');
  }
};
